import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaMinus, FaPlus } from "react-icons/fa";

import { getPublicMenus, getRestaurantProfile } from "../utils/api";

const BASE_URL = process.env.REACT_APP_API_BASE_URL_IMG;
function getFullImageUrl(imageUrl) {
  if (!imageUrl) return "";
  if (imageUrl.startsWith("http")) return imageUrl;
  const cleanUrl = imageUrl.replace(/^\/?uploads\//, "");
  return `${BASE_URL}/uploads/${cleanUrl}`;
}

export default function ParcelOrderModal({
  show,
  onClose,
  restaurantId,
  title,
  onSubmit,
  submitText,
  initialCart = [],
  initialType = "parcel",
  initialValue = "",
}) {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cart, setCart] = useState(initialCart);
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [orderType, setOrderType] = useState(initialType); // 'table', 'room', 'parcel'
  const [orderInput, setOrderInput] = useState(initialValue);
  const [searchTerm, setSearchTerm] = useState(""); // <-- Add search state

  // keep as string so user typing isn't auto-formatted into odd values (e.g. "010")
  const [discountPercent, setDiscountPercent] = useState(""); // NEW: discount %
  const [currentCaptainId, setCurrentCaptainId] = useState(null);

  const isUpdate = initialCart.length > 0;

  useEffect(() => {
    if (show) {
      // fetch current captain for this restaurant so we can pass captainId when placing order
      (async () => {
        try {
          if (restaurantId) {
            const profile = await getRestaurantProfile(restaurantId);
            setCurrentCaptainId(profile?.currentCaptainId || null);
          }
        } catch (err) {
          console.warn("Failed to load current captain:", err);
        }
      })();
      fetchMenus();
      setCart(initialCart);
      setOrderType(initialType);
      setOrderInput(initialValue);
      setDiscountPercent("");
    }
  }, [show]);

  async function fetchMenus() {
    setLoading(true);
    setError("");
    try {
      const data = await getPublicMenus(restaurantId);
      setMenus(data);
      if (data.length > 0) setSelectedMenuId(data[0].id);
    } catch (err) {
      setError("Failed to load menu");
    }
    setLoading(false);
  }

  function addToCart(item) {
    setCart((prev) => {
      const found = prev.find((i) => i.id === item.id);
      return found
        ? prev.map((i) => (i.id === item.id ? { ...i, qty: i.qty + 1 } : i))
        : [...prev, { ...item, qty: 1 }];
    });
  }

  function incrementQty(itemId) {
    setCart((prev) =>
      prev.map((i) => (i.id === itemId ? { ...i, qty: i.qty + 1 } : i))
    );
  }

  function decrementQty(itemId) {
    setCart((prev) => {
      const found = prev.find((i) => i.id === itemId);
      if (found?.qty === 1) {
        return prev.filter((i) => i.id !== itemId);
      }
      return prev.map((i) => (i.id === itemId ? { ...i, qty: i.qty - 1 } : i));
    });
  }

  function handleSubmit(cartItems) {
    if (!isUpdate) {
      if (orderType === "table" && !orderInput.trim()) {
        alert("Please enter a table number");
        return;
      }
      if (orderType === "room" && !orderInput.trim()) {
        alert("Please enter a room number");
        return;
      }

      // Pass captainId as last argument. Orders.handlePlaceParcelOrder / placeOrder should forward this to backend.
      onSubmit(
        cartItems,
        orderType,
        orderInput.trim(),
        Number(discountPercent || 0),
        currentCaptainId
      );
    } else {
      // update handler expects items only
      onSubmit(cartItems);
    }
  }

  if (!show) return null;

  const selectedMenu = menus.find((m) => m.id === selectedMenuId);
  // Filter and sort items by search term and lexicographically
  let filteredItems = [];
  if (!searchTerm) {
    // No search: show only items from selected category
    filteredItems = (selectedMenu?.items || [])
      .filter((i) => i.active)
      .sort((a, b) => a.name.localeCompare(b.name));
  } else {
    // Search: show all items from all categories that match the search term
    filteredItems = menus
      .flatMap((menu) => (menu.items || []).filter((i) => i.active))
      .filter((i) => i.name.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name));
  }
  const totalItems = cart.reduce((sum, item) => sum + item.qty, 0);
  const subtotal =
    Math.round(
      cart.reduce((sum, i) => sum + Number(i.price) * i.qty, 0) * 100
    ) / 100;
  const discount =
    Math.round(subtotal * (Number(discountPercent || 0) / 100) * 100) / 100;
  const adjustedSubtotal = Math.round((subtotal - discount) * 100) / 100;
  const tax = Math.round(adjustedSubtotal * 0.05 * 100) / 100;
  const grandTotal = Math.round((adjustedSubtotal + tax) * 100) / 100;

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col"
        initial={{ y: 20, scale: 0.95 }}
        animate={{ y: 0, scale: 1 }}
        exit={{ y: 20, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg lg:text-xl font-bold">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 text-2xl"
          >
            &times;
          </button>
        </div>
        {/* Order Type and Controls */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-2 lg:gap-4 px-4 lg:px-6 py-3 border-b bg-gray-50">
          {!isUpdate && (
            <div className="flex flex-wrap gap-2">
              <button
                className={`px-3 lg:px-4 py-1 rounded-full font-semibold border text-sm lg:text-base ${
                  orderType === "table"
                    ? "bg-rose-500 text-white border-rose-500"
                    : "bg-white text-gray-700 border-gray-300"
                }`}
                onClick={() => {
                  setOrderType("table");
                  setOrderInput("");
                }}
              >
                Table
              </button>
              <button
                className={`px-3 lg:px-4 py-1 rounded-full font-semibold border text-sm lg:text-base ${
                  orderType === "room"
                    ? "bg-rose-500 text-white border-rose-500"
                    : "bg-white text-gray-700 border-gray-300"
                }`}
                onClick={() => {
                  setOrderType("room");
                  setOrderInput("");
                }}
              >
                Room
              </button>
              <button
                className={`px-3 lg:px-4 py-1 rounded-full font-semibold border text-sm lg:text-base ${
                  orderType === "parcel"
                    ? "bg-rose-500 text-white border-rose-500"
                    : "bg-white text-gray-700 border-gray-300"
                }`}
                onClick={() => {
                  setOrderType("parcel");
                  setOrderInput("");
                }}
              >
                Parcel
              </button>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 lg:gap-4 w-full lg:w-auto">
            {orderType === "table" && (
              <input
                type="text"
                className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-rose-300 text-sm lg:text-base"
                placeholder="Enter Table Number"
                value={orderInput}
                onChange={(e) => setOrderInput(e.target.value)}
                style={{ width: "100%", maxWidth: 180 }}
              />
            )}
            {orderType === "room" && (
              <input
                type="text"
                className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-rose-300 text-sm lg:text-base"
                placeholder="Enter Room Number"
                value={orderInput}
                onChange={(e) => setOrderInput(e.target.value)}
                style={{ width: "100%", maxWidth: 180 }}
              />
            )}
            {orderType === "parcel" && (
              <input
                type="text"
                className="px-3 py-2 border rounded bg-gray-100 text-gray-400 text-sm lg:text-base"
                value="Parcel"
                disabled
                style={{ width: "100%", maxWidth: 120 }}
              />
            )}
            <input
              type="number"
              inputMode="numeric"
              step="1"
              min={0}
              max={100}
              className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-rose-300 text-sm lg:text-base"
              placeholder="Discount %"
              value={discountPercent}
              onChange={(e) => {
                let v = e.target.value.replace(/\D/g, "");
                if (v === "") {
                  setDiscountPercent("");
                  return;
                }
                let n = Math.min(100, Math.max(0, parseInt(v, 10) || 0));
                setDiscountPercent(String(n));
              }}
              style={{ width: "100%", maxWidth: 120 }}
            />
            <input
              type="text"
              className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-rose-300 text-sm lg:text-base"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: "100%", maxWidth: 200 }}
            />
          </div>
        </div>

        {/* Main POS Layout */}
        <div className="flex flex-col lg:flex-row flex-1 min-h-0">
          {/* Sidebar: Categories */}
          <aside
            className="w-full lg:w-1/5 bg-white border-r border-b lg:border-b-0 p-3 lg:p-4 flex flex-col overflow-hidden"
            // ensure the categories area can grow and scroll within the modal height
            style={{ minHeight: 0 }}
          >
            <div className="font-bold mb-2 text-rose-600 text-sm lg:text-base">
              Categories
            </div>
            <div className="flex gap-2 lg:flex-col lg:gap-1 overflow-x-auto lg:overflow-y-auto lg:overflow-x-visible">
              {menus.map((menu) => (
                <div
                  key={menu.id}
                  className={`p-2 cursor-pointer rounded mb-1 text-sm lg:text-base whitespace-nowrap lg:whitespace-normal ${
                    selectedMenuId === menu.id
                      ? "bg-rose-100 font-bold text-rose-700"
                      : "hover:bg-rose-50"
                  }`}
                  onClick={() => {
                    setSelectedMenuId(menu.id);
                    setSearchTerm("");
                  }}
                >
                  {menu.name}
                </div>
              ))}
            </div>
          </aside>

          {/* Items Grid */}
          <main className="w-full lg:w-3/5 p-3 lg:p-4 overflow-y-auto flex-1">
            {loading ? (
              <div className="flex justify-center items-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rose-500"></div>
              </div>
            ) : error ? (
              <div className="text-red-500 text-center py-8">{error}</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3 lg:gap-2">
                {filteredItems.map((item) => {
                  const cartItem = cart.find((i) => i.id === item.id);
                  return (
                    <div
                      key={item.id}
                      className="bg-white rounded-lg shadow p-2 flex flex-col items-center border hover:shadow-lg transition min-w-[120px] max-w-[160px] mx-auto"
                    >
                      {item.imageUrl && (
                        <img
                          src={getFullImageUrl(item.imageUrl)}
                          alt={item.name}
                          className="w-12 h-12 lg:w-16 lg:h-16 object-cover rounded mb-1"
                        />
                      )}
                      <div className="font-bold text-center text-gray-800 text-xs lg:text-sm truncate w-full">
                        {item.name}
                      </div>
                      <div className="text-rose-600 font-semibold mb-1 text-xs lg:text-sm">
                        ₹{item.price}
                      </div>
                      {cartItem ? (
                        <div className="flex items-center gap-1 mt-1">
                          <button
                            className="w-5 h-5 lg:w-6 lg:h-6 flex items-center justify-center rounded-full bg-rose-50 text-rose-500 hover:bg-rose-100 text-xs"
                            onClick={() => decrementQty(item.id)}
                          >
                            <FaMinus />
                          </button>
                          <span className="font-bold text-sm lg:text-base min-w-[16px] lg:min-w-[20px] text-center">
                            {cartItem.qty}
                          </span>
                          <button
                            className="w-5 h-5 lg:w-6 lg:h-6 flex items-center justify-center rounded-full bg-rose-50 text-rose-500 hover:bg-rose-100 text-xs"
                            onClick={() => incrementQty(item.id)}
                          >
                            <FaPlus />
                          </button>
                        </div>
                      ) : (
                        <button
                          className="mt-1 px-2 lg:px-3 py-1 bg-gradient-to-r from-rose-400 to-amber-400 text-white rounded-full font-semibold shadow hover:shadow-lg transition-all text-xs lg:text-sm"
                          onClick={() => addToCart(item)}
                        >
                          Add
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </main>

          {/* Cart Sidebar */}
          <aside className="w-full lg:w-1/4 bg-gray-50 border-t lg:border-t-0 lg:border-l p-4 lg:p-6 flex flex-col">
            <h3 className="text-base lg:text-lg font-bold mb-3 lg:mb-4">
              Cart ({totalItems})
            </h3>
            <div className="flex-grow overflow-y-auto space-y-2">
              {cart.length === 0 ? (
                <p className="text-gray-500 text-center mt-10 text-sm lg:text-base">
                  Cart is empty.
                </p>
              ) : (
                cart.map((item) => (
                  <div
                    key={item.id}
                    className="flex justify-between items-center mb-2 p-2 bg-white rounded shadow-sm"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-xs lg:text-sm truncate">
                        {item.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        ₹{item.price} x {item.qty}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 lg:gap-2 ml-2">
                      <button
                        className="w-5 h-5 lg:w-6 lg:h-6 flex items-center justify-center rounded-full bg-white text-rose-500 shadow-sm hover:shadow-md"
                        onClick={() => decrementQty(item.id)}
                      >
                        <FaMinus className="text-xs" />
                      </button>
                      <span className="px-1 min-w-[16px] lg:min-w-[20px] text-center text-xs lg:text-sm font-medium">
                        {item.qty}
                      </span>
                      <button
                        className="w-5 h-5 lg:w-6 lg:h-6 flex items-center justify-center rounded-full bg-white text-rose-500 shadow-sm hover:shadow-md"
                        onClick={() => incrementQty(item.id)}
                      >
                        <FaPlus className="text-xs" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="border-t pt-3 lg:pt-4 mt-3 lg:mt-4">
              <div className="flex justify-between font-bold text-base lg:text-lg">
                <span>Subtotal</span>
                <span>₹{subtotal.toFixed(2)}</span>
              </div>
              {Number(discountPercent) > 0 && (
                <div className="flex justify-between text-sm lg:text-base mt-1">
                  <span>Discount ({Number(discountPercent)}%)</span>
                  <span>-₹{discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-xs lg:text-sm mt-1">
                <span>Tax (5%)</span>
                <span>₹{tax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold text-lg lg:text-xl mt-2">
                <span>Total</span>
                <span>₹{grandTotal.toFixed(2)}</span>
              </div>
            </div>

            <button
              onClick={() => handleSubmit(cart)}
              disabled={cart.length === 0}
              className="w-full mt-4 lg:mt-6 py-2 lg:py-3 bg-gradient-to-r from-rose-500 to-amber-500 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transition-all disabled:bg-gray-300 text-sm lg:text-base"
            >
              {submitText}
            </button>
          </aside>
        </div>
      </motion.div>
    </motion.div>
  );
}
